import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { dleEvaluationApi } from '../lib/api.js';
import type { DleEvaluationTask, SubmitDleAnswerInput } from '../types/dle-evaluation.js';

/**
 * DLE 評価タスク一覧の query key。担当案件テーブルと一括回答ボタンが同じキーを共有し、
 * TanStack Query の dedup で 1 回のリクエストに畳まれる。
 * @graph-connects none
 */
const MY_DLE_TASKS_KEY = ['dle-evaluation', 'my-tasks'] as const;

/** 自分の DLE 評価集計の query key。 @graph-connects none */
const MY_DLE_SCORES_KEY = ['dle-evaluation', 'my-scores'] as const;

/**
 * 自分が回答すべき DLE 評価アンケート一覧を取得するフック。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 自分が PL/主担当の DLE 評価タスク一覧を TanStack Query 経由で取得する
 * @graph-connects dleEvaluationApi.getMyTasks [calls] DLE 評価タスク一覧 API
 */
export function useMyDleTasks(enabled = true) {
  return useQuery({
    queryKey: MY_DLE_TASKS_KEY,
    queryFn: () => dleEvaluationApi.getMyTasks(),
    staleTime: 60 * 1000,
    enabled,
  });
}

/**
 * 自分が DLE として受けた評価の集計を取得するフック。
 *
 * MyPage の「DLE評価」タブを出すかの判定 (`isDle`) もこのレスポンスで決まるので、
 * タブを開く前に (= PI Div メンバーの MyPage 表示時に) 1 回取得する。
 * サーバ側がシートを 60 秒キャッシュするため、`staleTime` もそれに合わせる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 自分の DLE 評価の月次/四半期系列と全体平均を TanStack Query 経由で取得する
 * @graph-connects dleEvaluationApi.getMyScores [calls] DLE 評価集計 API
 */
export function useMyDleScores(enabled = true) {
  return useQuery({
    queryKey: MY_DLE_SCORES_KEY,
    queryFn: () => dleEvaluationApi.getMyScores(),
    staleTime: 60 * 1000,
    enabled,
  });
}

/**
 * 指定した DLE の評価集計を取得するフック（管理者 / team_leader / PMOT 用）。
 *
 * `useMyDleScores` と分けているのは叩く API が別だから。あちらは権限不要で自分のぶんだけ、
 * こちらは `VIEW_DLE_EVALUATION` が要る。`dle` が null の間は呼ばない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 指定 DLE の評価集計を TanStack Query 経由で取得する（閲覧権限必須）
 * @graph-connects dleEvaluationApi.getScores [calls] 指定 DLE の評価集計 API
 */
export function useDleScores(dle: string | null, enabled = true) {
  return useQuery({
    queryKey: [...MY_DLE_SCORES_KEY, 'target', dle],
    // dle 未選択の間は skipToken でクエリごと止める。queryFn 内で dle が非 null に絞り込まれ、
    // 到達不能な `?? ''` フォールバックが要らなくなる
    queryFn: dle === null ? skipToken : () => dleEvaluationApi.getScores(dle),
    staleTime: 60 * 1000,
    enabled,
  });
}

/**
 * メールアドレスで指定したメンバーの DLE 評価集計を取得するフック。
 *
 * メンバー詳細ページ (`/members/$email`) 用。閲覧権限が無ければ 403 になるので、
 * 呼び出し側はデータが無いときタブを出さない。相手が DLE でない場合は
 * `isDle: false` の空系列が返る（これもタブを出さない判断に使う）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバーの DLE 評価集計を email 指定で取得する（閲覧権限必須）
 * @graph-connects dleEvaluationApi.getScoresByEmail [calls] email 指定の DLE 評価集計 API
 */
export function useDleScoresByEmail(email: string | null) {
  return useQuery({
    queryKey: [...MY_DLE_SCORES_KEY, 'email', email],
    // email 未確定 (null / 空文字) の間は skipToken で止める。useDleScores と同じく、
    // 到達不能な `?? ''` フォールバックを queryFn に持たせない
    queryFn:
      email === null || email === '' ? skipToken : () => dleEvaluationApi.getScoresByEmail(email),
    staleTime: 60 * 1000,
    // 閲覧権限が無ければ 403。リトライしても結果は変わらないので繰り返さない。
    retry: false,
  });
}

/**
 * DLE 評価回答を登録するミューテーションフック。
 *
 * 成功したら一覧を invalidate して「回答済」表示へ即座に切り替える。1 母票 1 回のみで
 * 編集できない仕様のため、楽観的更新はせずサーバの結果を待つ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business DLE 評価回答を登録し、タスク一覧を再取得して回答済み表示へ切り替える
 * @graph-connects dleEvaluationApi.submitAnswer [calls] DLE 評価回答登録 API
 */
export function useSubmitDleAnswer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SubmitDleAnswerInput) => dleEvaluationApi.submitAnswer(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MY_DLE_TASKS_KEY });
      // 自分が DLE の案件を自分で回答することはないが、同じ画面に自分の集計が
      // 出ているので、送信後に古い点が残らないよう両方 invalidate する。
      void queryClient.invalidateQueries({ queryKey: MY_DLE_SCORES_KEY });
    },
  });
}

/**
 * DLE 評価タスクを**母票キー**で引けるようにする。
 * 母票 1 件につきアンケートは 1 通なので、値は配列ではなく単数。
 * 開発 view の母票行がこの索引で自分のアンケートを 1 件だけ取る。
 * @graph-connects none
 */
export function indexDleTasksByParent(tasks: DleEvaluationTask[]): Map<string, DleEvaluationTask> {
  return new Map(tasks.map((task) => [task.parentIssueKey, task]));
}
